/**
 * useGroupInvite — iter127.
 *
 * Shared hook used by every "Share invite" entry point so the generation,
 * caching, share, copy, WhatsApp, and QR logic lives in ONE place.
 *
 * Usage:
 *   const inv = useGroupInvite(group);
 *   inv.openShareMenu();              // single tap → generate + open ShareSheet
 *   <ShareInviteSheet open={inv.open} onOpenChange={inv.setOpen} group={group} ... />
 *
 * Cache: codes are remembered per-group_id for the session so re-opening the
 * sheet is instant. Expiry/max-uses are enforced server-side.
 */
import { useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import api from './api';

export default function useGroupInvite() {
  const [open, setOpen] = useState(false);
  const [groupCtx, setGroupCtx] = useState(null);   // {id, name}
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const codeCache = useRef({});

  /** Generate (or reuse cached) invite for the given group, then open the sheet. */
  const openShareMenu = useCallback(async (group) => {
    if (!group?.id) return;
    setGroupCtx({ id: group.id, name: group.name });
    setOpen(true);
    const cached = codeCache.current[group.id];
    if (cached) { setCode(cached); return; }
    setCode('');
    setLoading(true);
    try {
      const res = await api.post(`/groups/${group.id}/invite`);
      const c = res.data?.code;
      if (c) {
        codeCache.current[group.id] = c;
        setCode(c);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate invite');
      setOpen(false);
    } finally { setLoading(false); }
  }, []);

  return { open, setOpen, code, loading, groupCtx, openShareMenu };
}
